import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { SharedModule } from '@shared/shared.module';
import {
  RequiredWindowResponse,
  YearCoverageBlockResponse,
} from '@interfaces/accounting-document-management/responses/document-coverage.response';
import { StatementViewMode } from '@features/dashboard/accounting-document-management/services/accounting-document-management-state.service';
import {
  buildPendencyChips,
  monthShortLabel,
  PendencyChip,
} from '@features/dashboard/accounting-document-management/utils/coverage-view.util';

/** Os três estados que o card de resumo distingue — ver {@link CoverageSummaryComponent.summaryState}. */
export type CoverageSummaryState = 'loading' | 'empty' | 'data';

/**
 * Card de resumo único da cobertura documental (PBI #2728, tela unificada): headline, subtítulo com a
 * regra da janela, barra de progresso, toggle de visualização (Régua/Cartões) e a faixa de chips de
 * pendência. Substitui os dois mecanismos que existiam por aba antes da unificação — o `statements-head`
 * da página (headline/próximo passo + toggle da aba "Demonstrações") e este próprio card, que só
 * atendia a aba "Documentos de faturamento".
 *
 * Componente de **apresentação pura**: recebe os exercícios já prontos (`YearCoverageBlockResponse`,
 * com as três trilhas — DRE, Balanço, Faturamento) e só **soma** o que o backend mandou
 * (`requiredCount`/`coveredCount` de cada exercício, que já somam as três trilhas). Nenhuma regra de
 * cobertura é recalculada aqui.
 *
 * **Defeito corrigido (fe-defeitos-do-print.md #1):** com `years` vazio — falha de carregamento,
 * empresa sem nenhum dado, ou ainda carregando — a soma de zero anos produzia `requiredCount = 0`, e
 * `completionPercent` (que existe para o caso legítimo de um *ano* sem exigência, regra do backend)
 * era lido como "100% coberto", pintando a barra verde cheia e "Nada pendente — cobertura completa."
 * para uma empresa sem documento nenhum. {@link summaryState} agora distingue os três estados
 * possíveis, e "cobertura completa" só é possível no estado `'data'` (que exige `requiredTotal > 0`).
 *
 * **Achado da revisão da Task 4, corrigido aqui:** somar `requiredCount`/`coveredCount` no nível do
 * exercício só ficou correto depois que o backend passou a somar as três trilhas nesses campos (antes
 * só contava o anual). O mesmo achado se aplicava aos chips — ver {@link buildPendencyChips}, que
 * agora varre as três trilhas em vez de só `annual`/`months` no nível do ano — então um exercício com
 * DRE e Balanço cobertos e só o Faturamento pendente aparece corretamente como incompleto, com um chip
 * nomeando o mês de faturamento em falta, em vez de cair no estado "cobertura completa".
 */
@Component({
  selector: 'app-coverage-summary',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './coverage-summary.component.html',
  styleUrl: './coverage-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoverageSummaryComponent {
  /** Exercícios de cobertura, já resolvidos e agrupados por trilha pelo backend. */
  readonly years = input.required<YearCoverageBlockResponse[]>();

  /** Janela obrigatória (3 anos anteriores + o corrente) — usada só para montar o texto do subtítulo. */
  readonly requiredWindow = input<RequiredWindowResponse | null>(null);

  /**
   * `true` enquanto a cobertura ainda está sendo buscada. Vem de
   * `AccountingDocumentManagementStateService.isLoadingCoverage` — só afeta {@link summaryState} quando
   * ainda não há nenhum ano em mãos ({@link years} vazio); com dados em cache (ex.: refetch após
   * upload) o resumo continua mostrando o que já tem, sem piscar para um spinner.
   */
  readonly isLoading = input(false);

  readonly viewMode = input.required<StatementViewMode>();

  readonly viewModeChange = output<StatementViewMode>();

  /** Emite o chip clicado — a página repassa o período (já resolvido pelo chip) para `stateService.selectPeriod`. */
  readonly chipSelected = output<PendencyChip>();

  readonly requiredTotal = computed(() =>
    this.years().reduce((sum, year) => sum + year.requiredCount, 0)
  );

  readonly coveredTotal = computed(() =>
    this.years().reduce((sum, year) => sum + year.coveredCount, 0)
  );

  /**
   * Estado do card: `'loading'` enquanto busca a primeira cobertura (sem nada em cache ainda);
   * `'empty'` quando a busca terminou (ou falhou) e não há nenhum período exigido para somar —
   * cobre os três cenários do defeito (falha de carregamento, empresa sem dado nenhum, e o vazio
   * legítimo); `'data'` só quando há pelo menos um período exigido, o único caso em que "cobertura
   * completa" pode ser afirmada.
   */
  readonly summaryState = computed<CoverageSummaryState>(() => {
    if (this.isLoading() && this.years().length === 0) return 'loading';
    if (this.requiredTotal() === 0) return 'empty';
    return 'data';
  });

  /**
   * Só chamado no estado `'data'` (`requiredTotal() > 0` garantido) — a ramificação de
   * `required === 0 → 100%` documentada no backend (um *ano* sem exigência está coberto) nunca é
   * exercida aqui porque {@link summaryState} já desvia esse caso para `'empty'` antes de chegar a
   * este cálculo.
   */
  readonly completionPercent = computed(() => {
    const required = this.requiredTotal();
    if (required === 0) return 0;
    return Math.round((this.coveredTotal() / required) * 100);
  });

  readonly pendencyChips = computed<PendencyChip[]>(() => buildPendencyChips(this.years()));

  readonly hasPendency = computed(() => this.pendencyChips().length > 0);

  /**
   * "X de Y períodos cobertos" — genérico de propósito: desde a unificação, cada exercício soma
   * períodos heterogêneos (o anual do DRE e do Balanço, mais os meses de Faturamento dentro da
   * janela), então "anos"/"meses" isolados não descrevem mais a conta.
   */
  readonly headline = computed(() => {
    if (this.summaryState() === 'empty') return 'Nenhum período de cobertura disponível';

    return `${this.coveredTotal()} de ${this.requiredTotal()} períodos cobertos`;
  });

  readonly subtitle = computed(() => {
    if (this.summaryState() === 'empty') {
      return 'Não há dados suficientes para calcular a cobertura desta empresa.';
    }

    const window = this.requiredWindow();
    if (!window) return '';

    const completeYears = window.years
      .filter(year => year !== window.currentYear)
      .sort((a, b) => a - b);
    const rangeLabel = this.formatYearRange(completeYears);

    // Em janeiro (`lastRequiredMonth === 0`), nenhum mês do ano corrente é exigido ainda — só o
    // range de anos completos entra no texto. Sem este ramo, `monthShortLabel(0)` cai fora dos
    // índices de `MONTH_SHORT_LABELS` e o subtítulo lia literalmente "jan a 0" (achado Minor da
    // revisão final da spec de cobertura de documentos contábeis).
    if (window.lastRequiredMonth === 0) {
      return rangeLabel
        ? `Exigido: ${rangeLabel} completos`
        : 'Nenhum período exigido ainda neste ano.';
    }

    const lastMonthLabel = monthShortLabel(window.lastRequiredMonth);

    return rangeLabel
      ? `Exigido: ${rangeLabel} completos + jan a ${lastMonthLabel} de ${window.currentYear}`
      : `Exigido: jan a ${lastMonthLabel} de ${window.currentYear}`;
  });

  setViewMode(mode: StatementViewMode): void {
    if (mode === this.viewMode()) return;
    this.viewModeChange.emit(mode);
  }

  onChipClick(chip: PendencyChip): void {
    this.chipSelected.emit(chip);
  }

  trackChip(chip: PendencyChip): string {
    return `${chip.kind}-${chip.year}-${chip.documentType}-${chip.startMonth}-${chip.endMonth}`;
  }

  private formatYearRange(years: number[]): string {
    if (years.length === 0) return '';
    if (years.length === 1) return String(years[0]);
    return `${years[0]}–${years[years.length - 1]}`;
  }
}
